/**
 * API Service Layer
 * 
 * This file contains all API calls to the backend.
 */

import {
  PRICE_INTELLIGENCE_FEED_ENABLED,
  PRICE_INTELLIGENCE_FEED_USE_MOCK,
  MERLIN_USE_MOCK,
  MERLIN_USE_VIC_CHAT,
} from '@/config/features';
import {
  buildMockNextDayPricePrediction,
  buildMockPriceIntelligenceFeed,
} from '@/mocks/priceIntelligenceFeedMock';
import type { CohortBoardPayload } from '@/components/CohortBoard';
import type { StockAnalogSnapshot } from '@/mocks/stockAnalogMock';

// Base API URL - configure based on environment
// When frontend is served from backend, use relative URLs (empty string)
// Otherwise, use the configured URL or default to localhost for development
// In production, default to fly.io backend if VITE_API_URL is not set or is empty
export function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  // If VITE_API_URL is explicitly set and not empty, use it
  if (envUrl && envUrl.trim() !== '') {
    return envUrl;
  }
  // Otherwise, use production fallback or localhost for development
  return import.meta.env.PROD ? 'https://mutual-friend-backend.fly.dev' : 'http://localhost:8000';
}

/** HTTP(S) API base → WebSocket base for live streams. */
export function apiBaseToWebSocketUrl(httpBase: string): string {
  if (httpBase.startsWith('https://')) {
    return `wss://${httpBase.slice('https://'.length)}`;
  }
  if (httpBase.startsWith('http://')) {
    return `ws://${httpBase.slice('http://'.length)}`;
  }
  return httpBase;
}

/**
 * WebSocket URL for Massive live price (direct to API host — avoids Vite WS proxy drops).
 */
export function getLivePriceWebSocketUrl(symbol: string): string {
  const sym = encodeURIComponent(symbol.trim().toUpperCase());
  const path = `/ws/stocks/${sym}/live-price`;
  return `${apiBaseToWebSocketUrl(getApiBaseUrl())}${path}`;
}

const API_BASE_URL = getApiBaseUrl();

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    let message = `API Error: ${response.status} ${response.statusText}`;
    try {
      const errorBody = await response.json();
      if (typeof errorBody?.detail === 'string') {
        message = errorBody.detail;
      }
    } catch {
      // Keep default message when response body is not JSON.
    }
    throw new Error(message);
  }

  return response.json();
}

/**
 * Authentication
 */
export const authService = {
  login: async (email: string, password: string) => {
    // TODO: Implement actual login
    console.log('Login attempt:', { email, password });
    return { token: 'mock-token', user: { email } };
  },

  logout: async () => {
    // TODO: Implement actual logout
    console.log('Logout');
  },

  register: async (email: string, password: string) => {
    // TODO: Implement actual registration
    console.log('Register attempt:', { email, password });
    return { success: true };
  },
};

export interface AccessRequestPayload {
  full_name: string;
  email: string;
}

export interface AccessRequestRecord {
  id: string;
  full_name: string;
  email: string;
  submitted_at: string;
}

export const accessRequestService = {
  submit: async (payload: AccessRequestPayload): Promise<AccessRequestRecord> => {
    return apiFetch<AccessRequestRecord>('/api/access-requests', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

/**
 * Earnings Data
 */
export interface UpcomingEarning {
  id: string;
  company: string;
  ticker: string;
  date: string;  // Formatted date (e.g., "Today", "Tomorrow", "Jan 15")
  rawDate: string;
  marketCap: string;
  rawMarketCap: number;
  epsEstimated: number | null;
  revenueEstimated: number | null;
  daysUntil?: number;  // Days until earnings (only in balanced mode)
  balancedScore?: number;      // Combined score (only in balanced mode)
  attractivenessScore?: number | null;  // Attractiveness score from composite.json
}

export interface UpcomingEarningsResponse {
  success: boolean;
  earnings: UpcomingEarning[];
  source_file?: string;
  total_upcoming?: number;
  fetched_at?: string;
  error?: string;
}

export const earningsService = {
  /**
   * Get upcoming earnings with balanced scoring
   * @param limit Number of results to return (default 5)
   * @param sortBy Sort mode: "balanced" (default), "market_cap", or "date"
   *   - "balanced": Combines date proximity and market cap (earnings soon get priority)
   *   - "market_cap": Pure market cap ranking
   *   - "date": Sort by date first, then market cap
   */
  getUpcoming: async (limit: number = 5, sortBy: string = 'balanced'): Promise<UpcomingEarning[]> => {
    try {
      const data = await apiFetch<UpcomingEarningsResponse>(
        `/api/upcoming-earnings?limit=${limit}&sort_by=${sortBy}`
      );
      
      if (!data.success || !data.earnings) {
        console.warn('No upcoming earnings data:', data.error);
        return [];
      }
      
      return data.earnings;
    } catch (error) {
      console.error('Error fetching upcoming earnings:', error);
      return [];
    }
  },

  getById: async (id: string) => {
    console.log('Fetching earning:', id);
    return null;
  },

  getByTicker: async (ticker: string) => {
    console.log('Fetching earnings for ticker:', ticker);
    return null;
  },
};

/**
 * Revenue Wall Guidance Comparison Types (matches backend JSON exactly)
 */
export interface RevenueWallGuidanceComparison {
  symbol: string;
  generated_at: string;
  analysis_method: string;
  quarters: RevenueQuarter[];
  summary: {
    total_quarters: number;
    quarters_with_management_guidance: number;
    quarters_with_wall_street_estimate: number;
    quarters_with_actual: number;
    guidance_accuracy_insights: string;
    quarters_with_yoy_calculated_guidance?: number;
    yoy_classification_method?: string;
    guidance_calculation_note?: string;
  };
}

export interface RevenueQuarter {
  quarter: string;
  reporting_date: string;
  management_guidance: {
    midpoint: number | null;
    midpoint_formatted: string | null;
    low: number | null;
    low_formatted: string | null;
    high: number | null;
    high_formatted: string | null;
    source?: string;
    classification_method?: string;
    growth_rate_used?: {
      low: number;
      high: number;
      midpoint: number;
      formatted_low: string;
      formatted_high: string;
      is_usd: boolean;
      is_constant_currency?: boolean;
    };
    base_quarter?: string;
    base_revenue?: number;
    base_revenue_formatted?: string;
    raw_guidance?: string;
    given_during_quarter?: string;
    given_on_date?: string;
    original_statement?: string;
    confidence_level?: string;
    guidance_source?: string;
    specific_metric_used?: string;
    ai_classification_reasoning?: string;
  } | null;
  wall_street_estimate: {
    value: number;
    formatted: string;
  } | null;
  actual_revenue: {
    value: number | null;
    formatted: string | null;
  } | null;
  comparisons: {
    actual_vs_wall_street?: {
      difference: number;
      difference_formatted: string;
      percentage: number;
      beat: boolean;
      description: string;
    } | null;
    actual_vs_management?: {
      difference: number;
      difference_formatted: string;
      percentage: number;
      actual_higher: boolean;
      description: string;
    } | null;
    wall_street_vs_management?: {
      difference: number;
      difference_formatted: string;
      percentage: number;
      wall_street_higher: boolean;
      description: string;
    } | null;
  } | null;
  ai_insights?: string;
  analyst_revisions?: {
    period_code: string;
    up_last_7_days: number;
    up_last_30_days: number;
    down_last_7_days: number;
    down_last_30_days: number;
  };
}

/**
 * Analysis Generation Response Types
 */
/**
 * Options Analysis Types (matches backend JSON exactly)
 */
export interface OptionsAnalysis {
  ticker: string;
  earnings_date?: string;
  analysis_date: string;
  current_price: number;
  primary_expiration: string;
  dte?: number;
  status: string;
  volume_and_open_interest: {
    call_volume: number;
    put_volume: number;
    put_call_volume_ratio: number;
    call_open_interest: number;
    put_open_interest: number;
    put_call_oi_ratio: number;
    positioning_interpretation: string;
  };
  expected_move: {
    estimated_move_pct: number;
    estimated_move_dollars: number;
    upper_target: number;
    lower_target: number;
    methodology: string;
    atm_straddle_price?: number;
    atm_strike?: number;
  };
  implied_volatility: {
    atm_call_iv: number;
    atm_put_iv: number;
    iv_average: number;
    iv_assessment: string;
  };
  historical_iv_context?: {
    status: string;
    current_iv_pct: number;
    iv_percentile_rank: number;
    iv_characterization: string;
    interpretation?: string;
  };
  confidence?: number;
  // Extended positioning signals
  iv_skew?: {
    otm_call_iv: number;
    otm_put_iv: number;
    skew_value: number;
    characterization: string;
  };
  max_pain?: {
    strike: number;
    distance_pct: number;
    interpretation: string;
  };
  gamma_exposure?: {
    total_gex: number;
    gex_flip_strike: number | null;
    characterization: string;
    top_strikes: Array<{ strike: number; gex: number; contract_type: string }>;
  };
  delta_positioning?: {
    net_delta_oi: number;
    call_delta_oi: number;
    put_delta_oi: number;
    directional_bias: string;
  };
  strike_walls?: {
    largest_call_wall: { strike: number; open_interest: number; contract_type: string } | null;
    largest_put_wall: { strike: number; open_interest: number; contract_type: string } | null;
  };
  probability_targets?: Array<{
    price: number;
    probability_pct: number;
    direction: 'above' | 'below';
    delta_basis: number;
    label?: string;
  }>;
  price_distribution?: Array<{
    low: number | null;
    high: number | null;
    probability_pct: number;
    label: string;
    is_atm: boolean;
  }>;
}

export interface OptionsAnalysisResponse {
  analysis_metadata: {
    ticker: string;
    earnings_date?: string;
    analysis_timestamp: string;
    current_price: number;
    options_plan_tier: string;
  };
  options_analysis: OptionsAnalysis;
  interpretation: {
    summary: string;
    expected_move_characterization: string;
    confidence_level: string;
    confidence_score: number;
    methodology_used: string;
    key_insights: string[];
    directional_bias?: string;
    ai_narrative?: string;
  };
}

/**
 * Stock Attractiveness Types (matches backend JSON exactly)
 */
export interface MetricComponent {
  name: string;
  raw_value: number;
  normalized_score: number;
  weight: number;
  interpretation: 'very_weak' | 'weak' | 'neutral' | 'strong' | 'very_strong';
  description: string;
  detailed_description?: string;
  grade?: string;
  is_applicable: boolean;
  sector_typical_range?: [number, number];
  why_it_matters?: string;
  calculation_details?: {
    quarterly_revenue?: Array<{
      date: string;
      period: string;
      fiscal_year: string;
      revenue: number;
    }>;
    quarterly_gross_profit?: Array<{
      date: string;
      period: string;
      fiscal_year: string;
      grossProfit: number;
    }>;
  };
}

export interface FinancialHealthResponse {
  symbol: string;
  layer_name: string;
  composite_score: number;
  academic_score?: number;  // Academic score (z-score normalized) for financial health
  academic_grade?: string;  // Letter grade (e.g., "C", "B-") for financial health
  interpretation: 'very_weak' | 'weak' | 'neutral' | 'strong' | 'very_strong';
  components: MetricComponent[];
  trend: string | null;
  trend_data: any;
  timestamp: string;
  metadata: {
    sector_type: string;
    sector_name: string;
    industry_name: string;
    model_name: string;
    model_description: string;
    limitations: string[];
    use_sector_models: boolean;
    academic_score?: number;  // Also in metadata for backwards compatibility
    academic_grade?: string;
  };
  error: string | null;
}

export interface LayerScore {
  score: number;
  academic_score?: number;  // Academic score (z-score normalized, only for financial_health)
  academic_grade?: string;  // Letter grade (e.g., "C", "B-", only for financial_health)
  interpretation: string;
  trend: string | null;
  components?: MetricComponent[];
  metadata?: {
    sector_type?: string;
    sector_name?: string;
    industry_name?: string;
    model_name?: string;
    model_description?: string;
    limitations?: string[];
    use_sector_models?: boolean;
    academic_score?: number;  // Also in metadata for backwards compatibility
    academic_grade?: string;
  };
}

export interface CompositeAttractivenessResponse {
  symbol: string;
  composite_score: number;
  composite_academic_score?: number;  // Academic score for overall composite
  composite_academic_grade?: string;  // Letter grade for overall composite
  interpretation: string;
  layer_breakdown: {
    financial_health: LayerScore;
    analyst_expectations: LayerScore;
    market_dynamics: LayerScore;
  };
  weights: {
    financial_health: number;
    analyst_expectations: number;
    market_dynamics: number;
  };
  trend_summary: string | null;
  signals: string[];
  timestamp: string;
  error: string | null;
  metadata?: any;  // Allow metadata for accessing cached data structures
}

export interface RecentAnalysis {
  symbol: string;
  score: number;
  interpretation: string;
  timestamp: string;
  file_modified: string;
  file_age_seconds: number;
}

export interface RecentAnalysesResponse {
  count: number;
  total_cached: number;
  analyses: RecentAnalysis[];
}

export interface AnalysisStatusResponse {
  symbol: string;
  available: boolean;
  generated_at?: string;
  message?: string;
  error?: string;
}

export interface AnalysisGenerationResponse {
  status: 'complete' | 'error';
  cached: boolean;
  symbol: string;
  duration_seconds?: number;
  data?: RevenueWallGuidanceComparison;
  error?: string;
}

/** Raw record from institutional_holders.json (per quarter) */
export interface InstitutionalHolderRecord {
  date?: string;
  cik?: string;
  filingDate?: string;
  investorName?: string;
  symbol?: string;
  typeOfSecurity?: string;
  putCallShare?: string;
  marketValue?: number;
  sharesNumber?: number;
  ownership?: number;
  firstAdded?: string;
  isSoldOut?: boolean;
  isNew?: boolean;
  changeInSharesNumber?: number;
  changeInSharesNumberPercentage?: number;
}

export interface InstitutionalHoldersQuarterBucket {
  fetched_at?: string;
  holders: InstitutionalHolderRecord[];
}

export interface InstitutionalHoldersFileResponse {
  success: boolean;
  symbol?: string;
  last_updated?: string;
  quarters?: Record<string, InstitutionalHoldersQuarterBucket>;
  error?: string;
}

/** One notable OPRA print per session day (daily raw tape or {TICKER}_options_trades_summary.json fallback). */
export interface NotableOptionTradeRow {
  date: string;
  time: string;
  symbol: string;
  option_type: 'C' | 'P' | '?';
  strike: number | null;
  expiry: string | null;
  price: number;
  size: number;
  premium: number;
  side: string;
}

/** One row from FMP stable insider-trading search (see fmp_insider_trading_search.json). */
export interface FmpInsiderTradeRow {
  symbol?: string;
  filingDate?: string;
  transactionDate?: string;
  reportingCik?: string;
  companyCik?: string;
  transactionType?: string;
  securitiesOwned?: number;
  reportingName?: string;
  typeOfOwner?: string;
  acquisitionOrDisposition?: string;
  directOrIndirect?: string | null;
  formType?: string;
  securitiesTransacted?: number;
  price?: number;
  securityName?: string;
  url?: string;
}

export interface FmpInsiderTradingFileResponse {
  success: boolean;
  symbol?: string;
  fetched_at?: string;
  source?: string;
  limit_per_page?: number;
  pages_fetched?: number[];
  row_count?: number;
  trades?: FmpInsiderTradeRow[];
  error?: string;
}

/** One row from FMP stable house-trades endpoint (see fmp_house_trades.json). */
export interface FmpHouseTradeRow {
  symbol?: string;
  disclosureDate?: string;
  transactionDate?: string;
  firstName?: string;
  lastName?: string;
  office?: string;
  district?: string;
  owner?: string;
  assetDescription?: string;
  assetType?: string;
  type?: string;
  amount?: string;
  capitalGainsOver200USD?: string;
  comment?: string;
  link?: string;
}

export interface FmpHouseTradesFileResponse {
  success: boolean;
  symbol?: string;
  fetched_at?: string;
  source?: string;
  row_count?: number;
  house_trades?: FmpHouseTradeRow[];
  error?: string;
}

/** Senate or House row with chamber tag (see fmp_congress_trades.json). */
export type FmpCongressTradeRow = FmpHouseTradeRow & {
  chamber: 'senate' | 'house';
};

export interface FmpCongressTradesFileResponse {
  success: boolean;
  symbol?: string;
  fetched_at?: string;
  sources?: Record<string, string>;
  fetch_stats?: Record<string, number>;
  data_coverage_note?: string;
  senate_row_count?: number;
  house_row_count?: number;
  row_count?: number;
  senate_trades?: FmpHouseTradeRow[];
  house_trades?: FmpHouseTradeRow[];
  congress_trades?: FmpCongressTradeRow[];
  error?: string;
}

/**
 * Stock Analysis Service
 */
export const stockService = {
  /**
   * 13F-style institutional holders from persistent_analyses/{symbol}/institutional_holders.json
   */
  getInstitutionalHolders: async (ticker: string): Promise<InstitutionalHoldersFileResponse | null> => {
    try {
      const data = await apiFetch<InstitutionalHoldersFileResponse>(
        `/api/stocks/${ticker.toUpperCase()}/institutional-holders`
      );
      return data;
    } catch (error) {
      console.error('Error fetching institutional holders:', error);
      return null;
    }
  },

  /**
   * FMP house-trades dump: persistent_analyses/{symbol}/fmp_house_trades.json
   */
  getFmpHouseTrades: async (ticker: string): Promise<FmpHouseTradesFileResponse | null> => {
    try {
      const data = await apiFetch<FmpHouseTradesFileResponse>(
        `/api/stocks/${ticker.toUpperCase()}/fmp-house-trades`
      );
      return data;
    } catch (error) {
      console.error('Error fetching FMP house trades:', error);
      return null;
    }
  },

  /**
   * FMP senate + house trades: persistent_analyses/{symbol}/fmp_congress_trades.json
   */
  getFmpCongressTrades: async (ticker: string): Promise<FmpCongressTradesFileResponse | null> => {
    try {
      const data = await apiFetch<FmpCongressTradesFileResponse>(
        `/api/stocks/${ticker.toUpperCase()}/fmp-congress-trades`
      );
      return data;
    } catch (error) {
      console.error('Error fetching FMP congress trades:', error);
      return null;
    }
  },

  /**
   * FMP insider-trading search dump: persistent_analyses/{symbol}/fmp_insider_trading_search.json
   */
  getFmpInsiderTrading: async (ticker: string): Promise<FmpInsiderTradingFileResponse | null> => {
    try {
      const data = await apiFetch<FmpInsiderTradingFileResponse>(
        `/api/stocks/${ticker.toUpperCase()}/fmp-insider-trading`
      );
      return data;
    } catch (error) {
      console.error('Error fetching FMP insider trading:', error);
      return null;
    }
  },

  /**
   * Fetch revenue wall guidance comparison data directly from backend
   * Returns the full JSON structure for display
   */
  getRevenueWallGuidanceComparison: async (ticker: string): Promise<RevenueWallGuidanceComparison | null> => {
    try {
      const data = await apiFetch<RevenueWallGuidanceComparison | { error: string }>(
        `/api/stocks/${ticker.toUpperCase()}/revenue-wall-guidance-comparison`
      );
      
      // Check if it's an error response
      if ('error' in data) {
        console.warn(`No revenue data for ${ticker}:`, data.error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching revenue wall guidance comparison:', error);
      return null;
    }
  },

  /**
   * Check if revenue wall guidance analysis exists for a ticker
   */
  checkAnalysisStatus: async (ticker: string): Promise<AnalysisStatusResponse> => {
    try {
      const data = await apiFetch<AnalysisStatusResponse>(
        `/api/stocks/${ticker.toUpperCase()}/revenue-wall-guidance-comparison/status`
      );
      return data;
    } catch (error) {
      console.error('Error checking analysis status:', error);
      return {
        symbol: ticker.toUpperCase(),
        available: false,
        error: 'Failed to check status'
      };
    }
  },

  /**
   * Generate revenue wall guidance comparison analysis
   * This triggers the full orchestrator pipeline and may take 1-3 minutes
   */
  generateAnalysis: async (ticker: string): Promise<AnalysisGenerationResponse> => {
    try {
      const data = await apiFetch<AnalysisGenerationResponse>(
        `/api/stocks/${ticker.toUpperCase()}/revenue-wall-guidance-comparison/generate`,
        { method: 'POST' }
      );
      return data;
    } catch (error) {
      console.error('Error generating analysis:', error);
      return {
        status: 'error',
        cached: false,
        symbol: ticker.toUpperCase(),
        error: error instanceof Error ? error.message : 'Failed to generate analysis'
      };
    }
  },

  getEarningsMetadata: async (ticker: string) => {
    console.log('Fetching earnings metadata for:', ticker);
    return null;
  },

  searchStocks: async (query: string) => {
    console.log('Searching stocks:', query);
    return [];
  },

  /**
   * Fetch options analysis data for a stock
   * Returns expected move, IV, and positioning data
   */
  getOptionsAnalysis: async (ticker: string): Promise<OptionsAnalysisResponse | null> => {
    try {
      const data = await apiFetch<OptionsAnalysisResponse | { error: string }>(
        `/api/stocks/${ticker.toUpperCase()}/options-analysis`
      );
      
      // Check if it's an error response
      if ('error' in data) {
        console.warn(`No options analysis for ${ticker}:`, data.error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching options analysis:', error);
      return null;
    }
  },

  /**
   * Fetch composite attractiveness score for a stock
   * Returns overall score combining financial health, analyst expectations, and market dynamics
   */
  getAttractivenessScore: async (ticker: string, forceFresh: boolean = false): Promise<CompositeAttractivenessResponse | null> => {
    try {
      const params = new URLSearchParams();
      if (forceFresh) {
        params.append('force_fresh', 'true');
      }
      
      const url = `/attractiveness/${ticker.toUpperCase()}${params.toString() ? '?' + params.toString() : ''}`;
      const data = await apiFetch<CompositeAttractivenessResponse>(url);
      
      if (data && data.composite_score !== undefined) {
        return data;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching attractiveness score:', error);
      return null;
    }
  },

  /**
   * Fetch financial health metrics for a stock (sector-specific)
   * Returns detailed metrics like Altman Z, Piotroski F, Rule of 40, etc.
   */
  getFinancialHealth: async (ticker: string): Promise<FinancialHealthResponse | null> => {
    try {
      const data = await apiFetch<FinancialHealthResponse>(
        `/attractiveness/${ticker.toUpperCase()}/financial-health`
      );
      
      if (data && data.composite_score !== undefined) {
        return data;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching financial health:', error);
      return null;
    }
  },

  /**
   * Fetch current stock price and company info from FMP
   */
  getStockPrice: async (ticker: string): Promise<{
    price: number;
    change: number;
    changePercent: number;
    companyName: string;
    sector?: string;
    industry?: string;
  } | null> => {
    try {
      const data = await apiFetch<any>(
        `/api/stocks/${ticker.toUpperCase()}/profile`
      );
      
      if (data && data.name) {
        return {
          price: data.price || 0,
          change: data.changes || 0,
          changePercent: data.changesPercentage || 0,
          companyName: data.name,
          sector: data.sector,
          industry: data.industry,
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching stock price:', error);
      return null;
    }
  },

  /**
   * Get recently analyzed stocks (most recent first)
   * Useful for discovering newly run analyses
   */
  getRecentAnalyses: async (limit: number = 10): Promise<RecentAnalysis[]> => {
    try {
      const data = await apiFetch<RecentAnalysesResponse>(
        `/attractiveness/recent-analyses?limit=${limit}`
      );
      
      if (data && data.analyses) {
        return data.analyses;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching recent analyses:', error);
      return [];
    }
  },

  /**
   * Mechanical (price-shape) analogs from up to 5y of FMP daily OHLC.
   */
  getMechanicalAnalogs: async (
    ticker: string,
    opts?: { date?: string; topK?: number; forceRefresh?: boolean },
  ): Promise<StockAnalogSnapshot | null> => {
    try {
      const params = new URLSearchParams();
      if (opts?.date) params.set('date', opts.date);
      if (opts?.topK != null) params.set('top_k', String(opts.topK));
      if (opts?.forceRefresh) params.set('force_refresh', 'true');
      const qs = params.toString();
      const data = await apiFetch<{
        success: boolean;
        data: StockAnalogSnapshot & {
          meta?: Record<string, unknown>;
        };
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/mechanical-analogs${qs ? `?${qs}` : ''}`);

      if (data?.success && data.data) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching mechanical analogs:', error);
      return null;
    }
  },

  /**
   * Situational (catalyst-story) analogs from curated catalyst_analogs_*.json.
   */
  getSituationalAnalogs: async (
    ticker: string,
    opts?: { date?: string; includeHonorableMention?: boolean },
  ): Promise<StockAnalogSnapshot | null> => {
    try {
      const params = new URLSearchParams();
      if (opts?.date) params.set('date', opts.date);
      if (opts?.includeHonorableMention) params.set('include_honorable_mention', 'true');
      const qs = params.toString();
      const data = await apiFetch<{
        success: boolean;
        data: StockAnalogSnapshot & {
          meta?: Record<string, unknown>;
        };
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/situational-analogs${qs ? `?${qs}` : ''}`);

      if (data?.success && data.data) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching situational analogs:', error);
      return null;
    }
  },

  /**
   * Sector-backdrop analogs (e.g. SOX for semiconductor names like SVCO).
   */
  getSectorAnalogs: async (
    ticker: string,
    opts?: { date?: string; topK?: number },
  ): Promise<StockAnalogSnapshot | null> => {
    try {
      const params = new URLSearchParams();
      if (opts?.date) params.set('date', opts.date);
      if (opts?.topK != null) params.set('top_k', String(opts.topK));
      const qs = params.toString();
      const data = await apiFetch<{
        success: boolean;
        data: StockAnalogSnapshot & {
          meta?: Record<string, unknown>;
        };
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/sector-analogs${qs ? `?${qs}` : ''}`);

      if (data?.success && data.data) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching sector analogs:', error);
      return null;
    }
  },

  /**
   * Fetch historical price data for a stock (up to 1 year)
   * Returns daily price data with open, high, low, close, volume
   */
  getHistoricalPrices: async (ticker: string, days: number = 365): Promise<Array<{
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    percent_change: number | null;
  }> | null> => {
    try {
      const data = await apiFetch<{
        success: boolean;
        data: Array<{
          date: string;
          open: number;
          high: number;
          low: number;
          close: number;
          volume: number;
          percent_change: number | null;
        }>;
        source?: string;
        total_days?: number;
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/historical-prices?days=${days}`);
      
      if (data && data.success && data.data) {
        return data.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching historical prices:', error);
      return null;
    }
  },

  /**
   * Fetch volume and trade_count from persistent_analyses for Intraday Price Movement.
   * Volume from price_data.json, trade_count from {symbol}_{date}_trades.json.
   */
  getIntradayStats: async (ticker: string, date: string): Promise<{
    volume: number | null;
    trade_count: number | null;
  } | null> => {
    try {
      const data = await apiFetch<{
        symbol: string;
        date: string;
        volume: number | null;
        trade_count: number | null;
      }>(`/api/stocks/${ticker.toUpperCase()}/intraday-stats/${encodeURIComponent(date)}`);

      if (data && (data.volume != null || data.trade_count != null)) {
        return { volume: data.volume ?? null, trade_count: data.trade_count ?? null };
      }
      return null;
    } catch (error) {
      console.error('Error fetching intraday stats:', error);
      return null;
    }
  },

  /**
   * Fetch 1-minute intraday price data for a stock on a specific date.
   * Default: Massive (Polygon) aggregate bars.
   * With publisherId: 1m OHLC prefers consolidated *_trades.json (venue-filtered), else mbo_pub, else MBP-10.
   * `tradeSource` describes bar construction, e.g. `massive_api`, `massive_api_duckdb_minute_markers`
   * (Polygon 1m line + DuckDB per-minute largest prints for markers), or Databento venue `trade_source`.
   */
  getIntradayPrices: async (
    ticker: string,
    date: string,
    publisherId?: number | null,
    options?: { minuteLine?: 'duckdb_largest_trade' },
  ): Promise<{
    bars: Array<{
      time: string;
      open: number;
      high: number;
      low: number;
      close: number;
      volume: number;
    }>;
    tradeSource?: string | null;
    publisherLabel?: string | null;
    /** Venue tick prints from cache when available (same source as intraday analytics). */
    publisherTickTrades?: Record<string, unknown>[] | null;
    /** When `minute_line=duckdb_largest_trade`: largest print per ET minute from DuckDB (diamond markers). */
    minuteLargestTrades?: Array<{
      time: string;
      price: number;
      size: number;
      side: string;
      venue?: number;
    }> | null;
  } | null> => {
    try {
      const q = new URLSearchParams({ date });
      if (publisherId != null && Number.isFinite(publisherId)) {
        q.set('publisher_id', String(Math.floor(publisherId)));
      }
      if (options?.minuteLine === 'duckdb_largest_trade') {
        q.set('minute_line', 'duckdb_largest_trade');
      }
      const data = await apiFetch<{
        success: boolean;
        symbol: string;
        date: string;
        bars: Array<{
          time: string;
          open: number;
          high: number;
          low: number;
          close: number;
          volume: number;
        }>;
        publisher_id?: number;
        publisher_label?: string | null;
        trade_source?: string | null;
        publisher_tick_trades?: Record<string, unknown>[] | null;
        minute_largest_trades?: Array<{
          time: string;
          price: number;
          size: number;
          side: string;
          venue?: number;
        }> | null;
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/intraday-prices?${q.toString()}`);

      if (data?.success && data.bars) {
        return {
          bars: data.bars,
          tradeSource: data.trade_source ?? null,
          publisherLabel: data.publisher_label ?? null,
          publisherTickTrades: data.publisher_tick_trades ?? null,
          minuteLargestTrades: data.minute_largest_trades ?? null,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching intraday prices:', error);
      return null;
    }
  },

  /**
   * Fetch hedge-fund grade intraday analytics computed from tick-level trade data.
   * Includes per-minute VWAP, cumulative delta, buy/sell ratios, and block trades.
   */
  getIntradayTradeAnalytics: async (ticker: string, date: string): Promise<{
    /** True when *_trades_top5.json had prints outside 9:30–4:00 PM ET so analytics include extended minutes. */
    session_et_extended?: boolean;
    summary: {
      session_vwap: number | null;
      total_volume: number;
      buy_volume: number;
      sell_volume: number;
      neutral_volume: number;
      buy_pct: number | null;
      sell_pct: number | null;
      net_delta: number;
      block_trade_count: number;
      block_trade_volume: number;
      block_trade_notional_usd?: number;
      trade_count?: number;
      session_notional_usd?: number;
    };
    per_minute: Array<{
      time: string;
      buy_vol: number;
      sell_vol: number;
      neutral_vol: number;
      volume: number;
      delta: number;
      cumulative_delta: number;
      vwap: number | null;
    }>;
    block_trades: Array<{
      time: string;
      price: number;
      size: number;
      side: string;
      venue: number;
    }>;
    venue_volume: Array<{
      publisher_id: number;
      volume: number;
      trade_count?: number;
      notional_usd?: number;
      label?: string | null;
    }>;
    /** Full consolidated tick list (optional payload from analytics). */
    ticks?: Record<string, unknown>[] | null;
  } | null> => {
    try {
      const data = await apiFetch<{
        success: boolean;
        symbol: string;
        date: string;
        summary: {
          session_vwap: number | null;
          total_volume: number;
          buy_volume: number;
          sell_volume: number;
          neutral_volume: number;
          buy_pct: number | null;
          sell_pct: number | null;
          net_delta: number;
          block_trade_count: number;
          block_trade_volume: number;
          block_trade_notional_usd?: number;
          trade_count?: number;
          session_notional_usd?: number;
        };
        per_minute: Array<{
          time: string;
          buy_vol: number;
          sell_vol: number;
          neutral_vol: number;
          volume: number;
          delta: number;
          cumulative_delta: number;
          vwap: number | null;
        }>;
        block_trades: Array<{
          time: string;
          price: number;
          size: number;
          side: string;
          venue: number;
        }>;
        venue_volume: Array<{
          publisher_id: number;
          volume: number;
          trade_count?: number;
          notional_usd?: number;
          label?: string | null;
        }>;
        ticks?: Record<string, unknown>[] | null;
        session_et_extended?: boolean;
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/intraday-trades/${encodeURIComponent(date)}`);

      if (data?.success) {
        return {
          session_et_extended: data.session_et_extended === true,
          summary: data.summary,
          per_minute: data.per_minute,
          block_trades: data.block_trades,
          venue_volume: data.venue_volume,
          ticks: data.ticks ?? null,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching intraday trade analytics:', error);
      return null;
    }
  },

  /**
   * Aggregated volume at each price level across a date range (yearly profile).
   * Powers the Volume by Price chart layer (horizontal bars on the price axis).
   */
  getYearlyPriceVolume: async (
    ticker: string,
    start: string,
    end: string,
    session: 'extended' | 'regular_hours' = 'extended',
    bucketCents = 25,
  ): Promise<import('@/utils/volumeByPriceLayer').YearlyPriceVolumePayload | null> => {
    try {
      const q = new URLSearchParams({
        start,
        end,
        session,
        bucket_cents: String(bucketCents),
      });
      const data = await apiFetch<
        import('@/utils/volumeByPriceLayer').YearlyPriceVolumePayload & {
          success?: boolean;
          error?: string;
        }
      >(
        `/api/stocks/${ticker.toUpperCase()}/yearly-price-volume?${q.toString()}`,
      );
      if (data?.success !== false && Array.isArray(data?.price_levels) && data.price_levels.length > 0) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching yearly price volume:', error);
      return null;
    }
  },

  /** Precomputed tape profile stats (price distribution, VWAP mix, etc.). */
  getTradeSessionStats: async (
    ticker: string,
    date: string,
  ): Promise<TradeSessionStatsPayload | null> => {
    try {
      const data = await apiFetch<TradeSessionStatsPayload>(
        `/api/stocks/${ticker.toUpperCase()}/trade-session-stats/${encodeURIComponent(date)}`,
      );
      return data ?? null;
    } catch (error) {
      console.error('Error fetching trade session stats:', error);
      return null;
    }
  },

  /**
   * AI feed evaluating price action and short-term direction for a session date.
   */
  getPriceIntelligenceFeed: async (
    ticker: string,
    body: {
      date: string;
      ohlc?: {
        open?: number;
        high?: number;
        low?: number;
        close: number;
        volume?: number;
        pct_change?: number;
      };
      recent_bars?: Array<{ date: string; close: number; pct_change?: number }>;
      force_refresh?: boolean;
    },
  ): Promise<PriceIntelligenceFeedPayload> => {
    if (PRICE_INTELLIGENCE_FEED_USE_MOCK) {
      return buildMockPriceIntelligenceFeed(ticker, body);
    }
    if (!PRICE_INTELLIGENCE_FEED_ENABLED) {
      return {
        success: false,
        symbol: ticker.toUpperCase(),
        date: body.date,
        headline: '',
        items: [],
        generated_at: new Date().toISOString(),
        error: 'Price intelligence feed is temporarily disabled',
      };
    }
    try {
      return await apiFetch<PriceIntelligenceFeedPayload>(
        `/api/stocks/${ticker.toUpperCase()}/price-intelligence-feed`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      );
    } catch (error) {
      console.error('Error fetching price intelligence feed:', error);
      return {
        success: false,
        symbol: ticker.toUpperCase(),
        date: body.date,
        headline: '',
        items: [],
        generated_at: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Failed to load feed',
      };
    }
  },

  /**
   * Cached next-session market_view forecast (Wyckoff state + thesis) for a session date.
   */
  getNextDayPricePrediction: async (
    ticker: string,
    asOfDate: string,
  ): Promise<NextDayPricePredictionPayload | null> => {
    if (PRICE_INTELLIGENCE_FEED_USE_MOCK) {
      return buildMockNextDayPricePrediction(ticker, asOfDate);
    }
    try {
      const params = new URLSearchParams({ as_of: asOfDate });
      return await apiFetch<NextDayPricePredictionPayload>(
        `/api/stocks/${ticker.toUpperCase()}/next-day-price-prediction?${params}`,
      );
    } catch (error) {
      console.error('Error fetching next-day price prediction:', error);
      return null;
    }
  },

  /**
   * Re-run next-day forecast with after-hours context (Massive AH + optional live override).
   */
  refreshNextDayPricePrediction: async (
    ticker: string,
    body: {
      as_of: string;
      after_hours_close?: number;
      include_after_hours?: boolean;
      refresh_after_hours_cache?: boolean;
      model?: string;
    },
  ): Promise<NextDayPricePredictionPayload | null> => {
    if (PRICE_INTELLIGENCE_FEED_USE_MOCK) {
      return {
        ...buildMockNextDayPricePrediction(ticker, body.as_of),
        from_cache: false,
        generated_at: new Date().toISOString(),
      };
    }
    try {
      return await apiFetch<NextDayPricePredictionPayload>(
        `/api/stocks/${ticker.toUpperCase()}/next-day-price-prediction/refresh`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      );
    } catch (error) {
      console.error('Error refreshing next-day price prediction:', error);
      return null;
    }
  },

  /**
   * Per-session-day buy vs sell aggressor share totals (full ET calendar day).
   * Used for the historical chart Imbalance layer.
   */
  getTapeImbalanceSeries: async (
    ticker: string,
    start: string,
    end: string,
  ): Promise<{
    points: Array<{
      date: string;
      buy_volume: number;
      sell_volume: number;
      neutral_volume: number;
    }>;
    from_duckdb: boolean;
  } | null> => {
    try {
      const q = new URLSearchParams({ start, end });
      const data = await apiFetch<{
        success: boolean;
        points: Array<{
          date: string;
          buy_volume: number;
          sell_volume: number;
          neutral_volume: number;
        }>;
        from_duckdb: boolean;
      }>(
        `/api/stocks/${ticker.toUpperCase()}/tape-imbalance-series?${q.toString()}`,
      );
      if (data?.success && Array.isArray(data.points)) {
        return { points: data.points, from_duckdb: data.from_duckdb === true };
      }
      return null;
    } catch (error) {
      console.error('Error fetching tape imbalance series:', error);
      return null;
    }
  },

  /**
   * Per-session peak volume price from trade_session_stats JSON (RTH).
   * Used for the Peak Volume chart layer on the historical chart.
   */
  getPeakVolumePriceSeries: async (
    ticker: string,
    start: string,
    end: string,
  ): Promise<{
    points: Array<{
      date: string;
      price: number;
      volume: number;
      pct_of_session_volume?: number | null;
    }>;
  } | null> => {
    try {
      const q = new URLSearchParams({ start, end });
      const data = await apiFetch<{
        success: boolean;
        points: Array<{
          date: string;
          price: number;
          volume: number;
          pct_of_session_volume?: number | null;
        }>;
      }>(
        `/api/stocks/${ticker.toUpperCase()}/peak-volume-price-series?${q.toString()}`,
      );
      if (data?.success && Array.isArray(data.points)) {
        return { points: data.points };
      }
      return null;
    } catch (error) {
      console.error('Error fetching peak volume price series:', error);
      return null;
    }
  },

  /**
   * Fetch footprint heatmap data for a specific ticker and date from DuckDB tick data.
   * Returns a price × time grid with buy/sell/neutral volume per cell.
   */
  getTradeHeatmap: async (
    ticker: string,
    date: string,
    opts?: { timeBucketMinutes?: number; priceTick?: number; includeExtended?: boolean },
  ): Promise<import('../components/TradeFootprintHeatmap').TradeHeatmapData | null> => {
    try {
      const params = new URLSearchParams();
      if (opts?.timeBucketMinutes) params.set('time_bucket_minutes', String(opts.timeBucketMinutes));
      if (opts?.priceTick) params.set('price_tick', String(opts.priceTick));
      if (opts?.includeExtended) params.set('include_extended', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await apiFetch<import('../components/TradeFootprintHeatmap').TradeHeatmapData>(
        `/api/stocks/${ticker.toUpperCase()}/trade-heatmap/${encodeURIComponent(date)}${qs}`,
      );
      return data ?? null;
    } catch (error) {
      console.error('Error fetching trade heatmap:', error);
      return null;
    }
  },

  /**
   * Fetch dates with largest block trade volume per month for historical chart overlay.
   */
  getBlockTradeDates: async (ticker: string, days: number = 365): Promise<string[] | null> => {
    try {
      const data = await apiFetch<{
        success: boolean;
        dates: string[];
        source?: string;
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/block-trade-dates?days=${days}`);
      if (data?.success && data.dates) return data.dates;
      return null;
    } catch (error) {
      console.error('Error fetching block trade dates:', error);
      return null;
    }
  },

  /**
   * Per-date sum of block-trade notional USD from persistent *_trades.json
   * (each trade with notional >= threshold, regular hours).
   */
  getBlockTradeNotionalByDate: async (
    ticker: string,
    days: number = 1825
  ): Promise<Record<string, number> | null> => {
    try {
      const data = await apiFetch<{
        success: boolean;
        notional_usd_by_date?: Record<string, number>;
        threshold_usd?: number;
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/block-trade-notional-by-date?days=${days}`);
      if (data?.success && data.notional_usd_by_date !== undefined) {
        return data.notional_usd_by_date;
      }
      return null;
    } catch (error) {
      console.error('Error fetching block trade notional by date:', error);
      return null;
    }
  },

  /**
   * Fetch largest block trades from persistent cache (largest_block_trades.json).
   * Used for historical chart block trade overlay with exact (date, price) positions.
   */
  /**
   * Per-session-day call vs put premium totals from options trade summary JSON.
   * Used for the historical chart Option Imbalance layer.
   */
  getOptionImbalanceSeries: async (
    ticker: string,
    start: string,
    end: string,
  ): Promise<{
    points: Array<{
      date: string;
      call_premium: number;
      put_premium: number;
      unknown_premium: number;
    }>;
    source: string | null;
  } | null> => {
    try {
      const q = new URLSearchParams({ start, end });
      const data = await apiFetch<{
        success: boolean;
        points: Array<{
          date: string;
          call_premium: number;
          put_premium: number;
          unknown_premium: number;
        }>;
        source?: string | null;
      }>(
        `/api/stocks/${ticker.toUpperCase()}/option-imbalance-series?${q.toString()}`,
      );
      if (data?.success && Array.isArray(data.points)) {
        return { points: data.points, source: data.source ?? null };
      }
      return null;
    } catch (error) {
      console.error('Error fetching option imbalance series:', error);
      return null;
    }
  },

  getNotableOptionTrades: async (
    ticker: string,
    minPremiumUsd = 25_000,
  ): Promise<{
    success: boolean;
    symbol?: string;
    min_premium_usd?: number;
    notable_option_trades_per_day?: NotableOptionTradeRow[];
    days_with_data?: number;
    error?: string;
  } | null> => {
    try {
      const data = await apiFetch<{
        success: boolean;
        symbol?: string;
        min_premium_usd?: number;
        notable_option_trades_per_day?: NotableOptionTradeRow[];
        days_with_data?: number;
        error?: string;
      }>(
        `/api/stocks/${ticker.toUpperCase()}/notable-option-trades?min_premium_usd=${minPremiumUsd}`,
      );
      return data ?? null;
    } catch (error) {
      console.error('Error fetching notable option trades:', error);
      return null;
    }
  },

  getLargestBlockTrades: async (ticker: string): Promise<{
    top_block_trades: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }>;
    largest_block_trade_per_month?: Array<{ month: string; trade: { date: string; time: string; price: number; size: number; dollar_value: number; side: string } }>;
    largest_block_trade_per_day?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }>;
    /** Statistically significant (IQR upper-fence outlier) subset of largest_block_trade_per_day. */
    noteworthy_block_trades_per_day?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }>;
    /** noteworthy ∪ significant-price/volume days — primary source for chart diamonds. */
    diamond_block_trades_per_day?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }>;
    total_block_trades: number;
  } | null> => {
    try {
      const data = await apiFetch<{
        success: boolean;
        top_block_trades?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }>;
        largest_block_trade_per_month?: Array<{ month: string; trade: { date: string; time: string; price: number; size: number; dollar_value: number; side: string } }>;
        largest_block_trade_per_day?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }>;
        noteworthy_block_trades_per_day?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }>;
        diamond_block_trades_per_day?: Array<{ date: string; time: string; price: number; size: number; dollar_value: number; side: string }>;
        total_block_trades?: number;
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/largest-block-trades`);
      if (data?.success && data.top_block_trades !== undefined) {
        return {
          top_block_trades: data.top_block_trades,
          largest_block_trade_per_month: data.largest_block_trade_per_month,
          largest_block_trade_per_day: data.largest_block_trade_per_day,
          noteworthy_block_trades_per_day: data.noteworthy_block_trades_per_day,
          diamond_block_trades_per_day: data.diamond_block_trades_per_day,
          total_block_trades: data.total_block_trades ?? data.top_block_trades.length,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching largest block trades:', error);
      return null;
    }
  },

  /**
   * Fetch significant moves (largest price changes) for a stock
   * Returns dates and percent changes for the top N largest moves
   */
  getSignificantMoves: async (ticker: string, days: number = 365, topN: number = 10): Promise<Array<{
    date: string;
    percent_change: number;
  }> | null> => {
    try {
      const data = await apiFetch<{
        success: boolean;
        moves: Array<{
          date: string;
          percent_change: number;
          abs_percent_change: number;
        }>;
        source?: string;
        total_days?: number;
        error?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/significant-moves?days=${days}&top_n=${topN}`);
      
      if (data && data.success && data.moves) {
        return data.moves.map(move => ({
          date: move.date,
          percent_change: move.percent_change
        }));
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching significant moves:', error);
      return null;
    }
  },

  /**
   * Dates where a new origin_event starts (ohlc_database_enriched.json).
   * Used for Significant Moves → Origin event chart dots.
   */
  getOriginEventDates: async (ticker: string): Promise<string[]> => {
    try {
      const data = await apiFetch<{
        success: boolean;
        dates: string[];
      }>(`/api/stocks/${ticker.toUpperCase()}/origin-event-dates`);
      if (data?.success && Array.isArray(data.dates)) {
        return data.dates;
      }
      return [];
    } catch (error) {
      console.error('Error fetching origin event dates:', error);
      return [];
    }
  },

  /**
   * Fetch Stock_Story data for a specific ticker and date
   * Returns executive_summary and other story data
   */
  getStockStory: async (ticker: string, date: string): Promise<{
    symbol: string;
    date: string;
    executive_summary: string | null;
    move_header?: string | null;
    metadata?: any;
    decision_tree_synthesis?: any;
    high_significance_themes?: Array<{ theme: string; key_evidence: string }> | null;
    /** From *_trades_minute_metrics_storyline_*.json when present */
    tape_storyline?: { overall_arc: string | null; signal_summary: string | null } | null;
    error?: string;
  } | null> => {
    try {
      const url = `/api/stocks/${ticker.toUpperCase()}/stock-story/${date}`;
      console.log('Fetching Stock Story from:', url);
      
      const data = await apiFetch<{
        symbol: string;
        date: string;
        executive_summary: string | null;
        move_header?: string | null;
        metadata?: any;
        decision_tree_synthesis?: any;
        high_significance_themes?: Array<{ theme: string; key_evidence: string }> | null;
        tape_storyline?: { overall_arc: string | null; signal_summary: string | null } | null;
      }>(url);
      
      console.log('Stock Story API response:', data);
      return data;
    } catch (error: any) {
      console.error('Error fetching Stock Story:', error);
      
      // Handle 404 specifically
      if (error?.message?.includes('404')) {
        return {
          symbol: ticker.toUpperCase(),
          date: date,
          executive_summary: null,
          tape_storyline: null,
          error: `Analysis is in progress. Insights will be provided shortly.`
        };
      }
      
      return {
        symbol: ticker.toUpperCase(),
        date: date,
        executive_summary: null,
        tape_storyline: null,
        error: error?.message || 'Failed to fetch Stock Story'
      };
    }
  },

  /**
   * Per-day entry from {TICKER}_decision_attribution.json (company vs market vs liquidity).
   */
  getDecisionAttributionDay: async (
    ticker: string,
    date: string,
  ): Promise<{
    date: string;
    ticker: string;
    price_change_pct?: number | null;
    primary_driver?: string | null;
    confidence?: string | null;
    driver_scores?: Record<string, number> | null;
    reasoning?: string | null;
    signals_available?: Record<string, boolean> | null;
  } | null> => {
    try {
      const data = await apiFetch<{
        success?: boolean;
        attribution?: {
          date?: string;
          ticker?: string;
          price_change_pct?: number | null;
          primary_driver?: string | null;
          confidence?: string | null;
          driver_scores?: Record<string, number> | null;
          reasoning?: string | null;
          signals_available?: Record<string, boolean> | null;
        };
      }>(`/api/stocks/${ticker.toUpperCase()}/decision-attribution/${date}`);
      const a = data?.attribution;
      if (data?.success && a && typeof a === 'object') {
        return a as {
          date: string;
          ticker: string;
          price_change_pct?: number | null;
          primary_driver?: string | null;
          confidence?: string | null;
          driver_scores?: Record<string, number> | null;
          reasoning?: string | null;
          signals_available?: Record<string, boolean> | null;
        };
      }
      return null;
    } catch {
      return null;
    }
  },

  /**
   * Dominant catalysts from Stock_Move_Analysis.json (per-date aggregator output).
   * Used for origin-event dates on the significant-moves chart.
   */
  getStockMoveDominantCatalysts: async (
    ticker: string,
    date: string,
  ): Promise<{
    dominant_catalysts: Array<{ rank: number; catalyst: string; mechanism: string }>;
    /** Optional override from Stock_Move_Analysis.json for origin-event chart card title */
    origin_event_card_title?: string | null;
  } | null> => {
    try {
      const data = await apiFetch<{
        success?: boolean;
        dominant_catalysts?: Array<{
          rank?: number;
          catalyst?: string;
          mechanism?: string;
        }>;
        origin_event_card_title?: string | null;
      }>(`/api/stocks/${ticker.toUpperCase()}/stock-move-analysis/${date}`);
      const parseOriginTitle = (d: typeof data) => {
        const t = d?.origin_event_card_title;
        return typeof t === 'string' && t.trim().length > 0 ? t.trim() : null;
      };
      const raw = data?.dominant_catalysts;
      if (!Array.isArray(raw)) {
        return { dominant_catalysts: [], origin_event_card_title: parseOriginTitle(data) };
      }
      const dominant_catalysts = raw
        .map((c) => ({
          rank: typeof c.rank === 'number' && !Number.isNaN(c.rank) ? c.rank : 0,
          catalyst: typeof c.catalyst === 'string' ? c.catalyst : '',
          mechanism: typeof c.mechanism === 'string' ? c.mechanism : '',
        }))
        .filter((c) => c.catalyst.length > 0 || c.mechanism.length > 0)
        .sort((a, b) => {
          const ar = a.rank || 9999;
          const br = b.rank || 9999;
          return ar - br;
        });
      return { dominant_catalysts, origin_event_card_title: parseOriginTitle(data) };
    } catch {
      return null;
    }
  },

  /**
   * Fetch Stock_Article_Snapshot data for a specific ticker and date
   * Returns selected articles with relevance scores for related coverage
   */
  getStockArticleSnapshot: async (ticker: string, date: string): Promise<{
    metadata: any;
    coverage_summary: any;
    selected_articles: Array<{
      title: string;
      source_name: string;
      news_url: string;
      relevance_score?: number;
      sentiment?: string;
      date: string;
      is_market_wide?: boolean;  // Flag to indicate if article is from market-wide sources
      content_length?: number;
    }>;
    error?: string;
  } | null> => {
    try {
      const url = `/api/stocks/${ticker.toUpperCase()}/stock-article-snapshot/${date}`;
      console.log('Fetching Stock Article Snapshot from:', url);
      
      const data = await apiFetch<{
        metadata: any;
        coverage_summary: any;
        selected_articles: Array<{
          title: string;
          source_name: string;
          news_url: string;
          relevance_score?: number;
          sentiment?: string;
          date: string;
        }>;
      }>(url);
      
      console.log('Stock Article Snapshot API response:', data);
      return data;
    } catch (error: any) {
      console.error('Error fetching Stock Article Snapshot:', error);
      
      // Handle 404 specifically
      if (error?.message?.includes('404')) {
        return {
          metadata: {},
          coverage_summary: {},
          selected_articles: [],
          error: `No Stock Article Snapshot found for ${date}`
        };
      }
      
      return {
        metadata: {},
        coverage_summary: {},
        selected_articles: [],
        error: error?.message || 'Failed to fetch Stock Article Snapshot'
      };
    }
  },

  /**
   * Trigger parallel processing of all dates in largest_moves.json
   * This starts background processing of the 13 largest monthly moves
   */
  processLargestMoves: async (ticker: string): Promise<{
    success: boolean;
    task_id: string;
    symbol: string;
    total_dates: number;
    dates: string[];
    message: string;
  } | null> => {
    try {
      const data = await apiFetch<{
        success: boolean;
        task_id: string;
        symbol: string;
        total_dates: number;
        dates: string[];
        message: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/process-largest-moves`, {
        method: 'POST'
      });
      
      console.log('Process largest moves response:', data);
      return data;
    } catch (error: any) {
      console.error('Error processing largest moves:', error);
      return null;
    }
  },

  /**
   * Get real-time progress of largest moves analysis
   * Poll this endpoint to monitor completion status
   */
  getAnalysisProgress: async (ticker: string): Promise<{
    symbol: string;
    status: string;
    progress: number;
    completed_moves: number;
    total_moves: number;
    completed_dates?: string[];
    completed_results?: Array<{
      date: string;
      percent_change: number;
      status: string;
      summary?: string;
    }>;
    message?: string;
  } | null> => {
    try {
      const data = await apiFetch<{
        symbol: string;
        status: string;
        progress: number;
        completed_moves: number;
        total_moves: number;
        completed_dates?: string[];
        completed_results?: Array<{
          date: string;
          percent_change: number;
          status: string;
          summary?: string;
        }>;
        message?: string;
      }>(`/api/stocks/${ticker.toUpperCase()}/analysis-progress`);
      
      return data;
    } catch (error: any) {
      console.error('Error getting analysis progress:', error);
      return null;
    }
  },
};

/**
 * Trending Stocks Types
 */
export interface TrendingStock {
  ticker: string;
  company: string;
  price: number | null;
  change: number | null;
  change_percent: number | null;
  score: number | null;
  price_timestamp?: string;
}

export interface TrendingStocksResponse {
  success: boolean;
  data: {
    last_updated: string;
    stocks: TrendingStock[];
  };
}

/**
 * Trending Stocks Service
 */
export const trendingService = {
  /**
   * Get trending stocks with aggregated data from cache
   * Returns stocks with prices, scores, and metadata
   */
  getTrendingStocks: async (): Promise<TrendingStock[]> => {
    try {
      const data = await apiFetch<TrendingStocksResponse>('/trending/stocks');
      
      if (!data.success || !data.data.stocks) {
        console.warn('No trending stocks data available');
        return [];
      }
      
      return data.data.stocks;
    } catch (error) {
      console.error('Error fetching trending stocks:', error);
      return [];
    }
  },

  /**
   * Refresh price data for trending stocks
   */
  refreshPrices: async (forceFresh: boolean = false): Promise<boolean> => {
    try {
      const data = await apiFetch<{ success: boolean }>(
        `/trending/refresh-prices?force_fresh=${forceFresh}`,
        { method: 'POST' }
      );
      return data.success;
    } catch (error) {
      console.error('Error refreshing trending prices:', error);
      return false;
    }
  },
};

export { apiFetch };

// ---------------------------------------------------------------------------
// Trade session stats (tape profile)
// ---------------------------------------------------------------------------

export type PriceIntelligenceFeedItem = {
  id: string;
  kind: 'read' | 'tape' | 'context' | 'outlook' | 'risk' | 'moment';
  title: string;
  body: string;
  sentiment: 'bullish' | 'bearish' | 'neutral';
  confidence: 'high' | 'medium' | 'low';
};

export type PriceIntelligenceFeedPayload = {
  success?: boolean;
  symbol: string;
  date: string;
  headline: string;
  items: PriceIntelligenceFeedItem[];
  generated_at: string;
  model?: string | null;
  sources_used?: string[];
  from_cache?: boolean;
  error?: string | null;
};

export type NextDayMarketView = {
  market_state: string;
  thesis_confidence_pct: number;
  current_thesis: string;
  invalidation: string;
  next_likely_move: string;
};

export type NextDayPricePredictionPayload = {
  success: boolean;
  symbol?: string;
  as_of_date?: string;
  target_date?: string;
  generated_at?: string;
  model?: string;
  market_view?: NextDayMarketView;
  prediction?: {
    target_date?: string;
    reference_date?: string;
    predicted_close?: number;
    predicted_change_pct?: number;
    direction?: string;
    confidence?: string;
  };
  from_cache?: boolean;
  error?: string;
};

export type TradeSessionStatsPayload = {
  success: boolean;
  symbol: string;
  date: string;
  deduped_trade_count?: number;
  raw_trade_count?: number;
  regular_hours?: import('@/utils/tradeSessionStatsDisplay').TradeSessionRegularHours;
  extended?: import('@/utils/tradeSessionStatsDisplay').TradeSessionRegularHours;
  error?: string;
};

// ---------------------------------------------------------------------------
// Time & Sales
// ---------------------------------------------------------------------------

export type TimeAndSalesFilter =
  | 'all'
  | 'chronological'
  | 'buys'
  | 'sells'
  | 'blocks'
  | 'largest';

export interface TimeAndSalesTrade {
  time: string;         // "HH:MM:SS" Eastern
  time_full: string;    // ISO with timezone
  price: number;
  size: number;
  notional: number;
  side: 'B' | 'A' | 'N';   // B = buy-aggressor, A = sell-aggressor, N = neutral
  is_block: boolean;
  publisher_id: number | null;
  sequence: number | null;
}

export interface TimeAndSalesResponse {
  success: boolean;
  symbol: string;
  date: string;
  filter: TimeAndSalesFilter;
  limit: number;
  total_matched: number;
  /** Sum of share sizes for prints in the same window as the tape (RTH unless extended; optional ET clock bounds). */
  day_total_volume: number;
  source: 'duckdb' | 'json_fallback' | 'none';
  trades: TimeAndSalesTrade[];
  error?: string;
  /** Resolved ET lower bound (HH:MM:SS) when a time window was applied. */
  time_start?: string | null;
  /** Resolved ET upper bound (HH:MM:SS) when a time window was applied. */
  time_end?: string | null;
}

// ---------------------------------------------------------------------------
// Polymarket
// ---------------------------------------------------------------------------

export interface PolymarketMarket {
  id: string;
  question: string;
  yes_probability: number | null;
  end_date: string;
  volume_24h: number;
  volume_total: number;
  liquidity: number;
  slug: string;
  image: string;
  relevance_reason: string;
}

/** One row of the ranked candidate list passed to GPT OSS 20B (index matches prompt [N]). */
export interface PolymarketPoolRow {
  index: number;
  id: string;
  question: string;
  yes_probability: number | null;
  end_date: string;
  volume_24h: number;
  slug: string;
  /** Polymarket browse bucket: finance | geopolitics | economy | tech (or global_volume if fallback). */
  pool_theme?: string;
  /** Parent event title from Gamma when the market came from `/events?tag_slug=...`. */
  event_title?: string;
}

export interface PolymarketEventsResponse {
  success: boolean;
  ticker: string;
  markets: PolymarketMarket[];
  /** Full pool the model ranked (Finance / Geopolitics / Economy / Tech tag feeds, unless fallback). */
  selection_pool?: PolymarketPoolRow[];
  selection_source?: 'llm' | 'heuristic';
  /** `themed_tags` = curated tag buckets; missing on older payloads. */
  pool_strategy?: string;
  pool_themes?: Array<{ key: string; tag_slug: string }>;
  per_theme_top?: number;
  events_fetched_per_theme?: number;
  cached_at: number;
  from_cache: boolean;
  error?: string;
}

export const polymarketService = {
  getEvents: async (
    ticker: string,
    opts: {
      company?: string;
      sector?: string;
      industry?: string;
      forceRefresh?: boolean;
      /** Set false to omit `selection_pool` (smaller response). Default true. */
      includeSelectionPool?: boolean;
    } = {},
  ): Promise<PolymarketEventsResponse | null> => {
    try {
      const params = new URLSearchParams();
      if (opts.company) params.set('company', opts.company);
      if (opts.sector) params.set('sector', opts.sector);
      if (opts.industry) params.set('industry', opts.industry);
      if (opts.forceRefresh) params.set('force_refresh', 'true');
      if (opts.includeSelectionPool === false) params.set('include_selection_pool', 'false');
      const qs = params.toString() ? `?${params}` : '';
      return await apiFetch<PolymarketEventsResponse>(
        `/api/stocks/${ticker.toUpperCase()}/polymarket-events${qs}`,
      );
    } catch (error) {
      console.error('Error fetching Polymarket events:', error);
      return null;
    }
  },
};

// ---------------------------------------------------------------------------
// Macro / Fed rate decision
// ---------------------------------------------------------------------------

export interface FedRateMarket {
  id: string;
  question: string;
  yes_probability: number | null;
  end_date: string;
  volume_24h: number;
  volume_total: number;
  liquidity: number;
  slug: string;
  image: string;
  description: string;
  event_title: string;
  polymarket_url: string;
  condition_id: string;
  /** YES outcome CLOB token id — required for /prices-history (not conditionId). */
  clob_yes_token_id: string;
}

export interface MarketProbHistory {
  success: boolean;
  yes_token_id?: string;
  history: Record<string, number>;
  cached_at: number;
  from_cache: boolean;
  error?: string;
}

export interface FedRateDecisionResponse {
  success: boolean;
  markets: FedRateMarket[];
  cached_at: number;
  from_cache: boolean;
  error?: string;
}

export const macroService = {
  getFedRateDecision: async (forceRefresh = false): Promise<FedRateDecisionResponse | null> => {
    try {
      const qs = forceRefresh ? '?force_refresh=true' : '';
      return await apiFetch<FedRateDecisionResponse>(`/api/macro/fed-rate-decision${qs}`);
    } catch (error) {
      console.error('Error fetching Fed rate decision markets:', error);
      return null;
    }
  },

  getMarketHistory: async (yesTokenId: string): Promise<MarketProbHistory | null> => {
    try {
      return await apiFetch<MarketProbHistory>(
        `/api/macro/market-history/${encodeURIComponent(yesTokenId)}`,
      );
    } catch (error) {
      console.error('Error fetching market probability history:', error);
      return null;
    }
  },
};

// ---------------------------------------------------------------------------
// Market predictions
// ---------------------------------------------------------------------------

export interface MarketPredictionIndexForecast {
  ticker: string;
  predicted_change_pct: number;
}

export interface MarketAnalysisTheme {
  rank?: number | null;
  theme?: string | null;
  significance?: string | null;
  direction?: string | null;
  mechanism?: string | null;
  market_impact?: string | null;
}

export interface MarketAnalysisSectorRow {
  sector: string;
  performance?: string | null;
  narrative?: string | null;
}

export interface MarketAnalysisSectorPerformance {
  synthesis?: string | null;
  top_3?: MarketAnalysisSectorRow[];
  bottom_3?: MarketAnalysisSectorRow[];
}

export interface MarketAnalysisExcerpt {
  reference_date: string;
  model?: string | null;
  aggregator?: string | null;
  overall_market_direction?: string | null;
  confidence_level?: string | null;
  excerpt?: string | null;
  dominant_themes?: MarketAnalysisTheme[];
  sector_performance?: MarketAnalysisSectorPerformance | null;
}

export interface MajorStory {
  rank?: number;
  title: string;
  direction?: string | null;
  summary?: string | null;
  body?: string | null;
  tags?: string[];
  analogue_reference_date?: string;
  lens?: import('@/mocks/stockAnalogMock').StockAnalogLensData;
}

export interface MajorStoriesMetadata {
  week_label?: string;
  start_date?: string;
  end_date?: string;
  trading_days?: number;
  source?: string;
}

export interface MajorStoriesPayload {
  success?: boolean;
  filename?: string;
  metadata?: MajorStoriesMetadata;
  stories: MajorStory[];
}

export interface MarketPredictionSummary {
  reference_date: string;
  target_date?: string | null;
  prediction_horizon?: string | null;
  generated_at?: string | null;
  data_as_of_session?: string | null;
  overall_direction?: string | null;
  predicted_spy_change_pct?: number | null;
  confidence_score?: number | null;
  regime?: string | null;
  thesis?: string | null;
  index_forecasts?: MarketPredictionIndexForecast[];
  filename?: string | null;
  analog_closest_date?: string | null;
  analog_similarity?: number | null;
  generator?: string | null;
  catalyst_event?: string | null;
  catalyst_surprise_bucket?: string | null;
  narrative_scenario_tilt?: string | null;
}

export interface MarketPredictionScorecardActual {
  ref_close?: number;
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  actual_change_pct?: number;
}

export interface MarketPredictionScorecardRow {
  symbol: string;
  label?: string | null;
  group?: 'index' | 'sector' | string | null;
  sector?: string | null;
  predicted_change_pct?: number | null;
  predicted_direction?: string | null;
  predicted_range_pct?: [number, number] | null;
  actual_change_pct?: number | null;
  actual_direction?: string | null;
  direction_correct?: boolean | null;
  range_correct?: boolean | null;
  error_pct?: number | null;
  actual?: MarketPredictionScorecardActual | null;
}

export interface MarketPredictionScorecardSummary {
  direction_correct?: number | null;
  direction_total?: number | null;
  combined_direction?: string | null;
  range_correct?: number | null;
  range_total?: number | null;
  combined_range?: string | null;
  combined_mae?: number | null;
}

export interface MarketPredictionScorecardMarket {
  predicted_overall_direction?: string | null;
  predicted_spy_change_pct?: number | null;
  actual_spy_change_pct?: number | null;
  actual_spy_direction?: string | null;
  direction_correct?: boolean | null;
}

export interface MarketPredictionScorecard {
  reference_date: string;
  target_date: string;
  generated_at?: string | null;
  prediction_file?: string | null;
  summary?: MarketPredictionScorecardSummary | null;
  market?: MarketPredictionScorecardMarket | null;
  rows?: MarketPredictionScorecardRow[];
}

export interface AnalogueCatalystCase {
  trigger?: string | null;
  market_path?: string | null;
  implication?: string | null;
  analogue?: string | null;
  spy_target_short_term?: string | null;
  spy_target_4_to_8_weeks?: string | null;
  probability?: string | null;
}

export interface AnalogueCatalyst {
  rank?: number;
  event?: string | null;
  date?: string | null;
  importance?: string | null;
  question?: string | null;
  context?: string | null;
  bull_case?: AnalogueCatalystCase | null;
  bear_case?: AnalogueCatalystCase | null;
}

export interface AnalogueSituational {
  date?: string | null;
  title?: string | null;
  similarity_type?: string | null;
  similarity_score?: number | null;
  similarity_label?: string | null;
  outcome?: string | null;
  outcome_direction?: string | null;
  day_stats?: Record<string, number> | null;
  fed_context?: string | null;
  context?: string | null;
  similarities?: string[];
  key_differences?: string[];
  what_happened_next?: string | null;
  key_lesson?: string | null;
  forward_implication?: string | null;
}

export interface AnalogueNumerical {
  pattern?: string | null;
  date?: string | null;
  similarity_type?: string | null;
  similarity_label?: string | null;
  context?: string | null;
  pattern_characteristics?: Record<string, unknown> | null;
  pattern_explanation?: string | null;
  pattern_verdict?: string | null;
  what_this_means_short_term?: string | null;
  historical_examples?: string[];
}

export interface AnalogueDangerTemplate {
  date?: string | null;
  title?: string | null;
  outcome?: string | null;
  outcome_direction?: string | null;
  day_stats?: Record<string, number> | null;
  fed_context?: string | null;
  context?: string | null;
  what_happened_next?: string | null;
  key_differences_from_today?: string[];
  why_this_matters?: string | null;
}

export interface AnalogueRegime {
  date?: string | null;
  title?: string | null;
  similarity_type?: string | null;
  similarity_label?: string | null;
  outcome?: string | null;
  outcome_direction?: string | null;
  macro_state?: Record<string, unknown> | null;
  context?: string | null;
  similarities?: string[];
  key_differences?: string[];
  what_happened_next?: string | null;
  forward_returns_20d?: Record<string, number | string> | null;
  key_lesson?: string | null;
  forward_implication?: string | null;
}

export interface AnalogueDangerWindow {
  title?: string | null;
  start?: string | null;
  end?: string | null;
  importance?: string | null;
  summary?: string | null;
  events?: { event?: string; why_it_matters?: string }[];
  downside_scenario?: string | null;
  upside_scenario?: string | null;
  verdict?: string | null;
}

export interface AnalogueCompositeVerdict {
  summary?: string | null;
  primary_analogue?: string | null;
  mechanical_analogue?: string | null;
  regime_analogue?: string | null;
  tail_risk_analogue?: string | null;
  base_case_direction?: string | null;
  risk_skew?: string | null;
  base_case_probability?: number | null;
  consolidation_case_probability?: number | null;
  bear_case_probability?: number | null;
  warsh_shock_probability?: number | null;
  key_branching_point?: string | null;
  watch_list?: string[];
  linkedin_hook?: string | null;
  confidence_note?: string | null;
}

export interface MarketAnalogueAnalysis {
  metadata?: {
    reference_date?: string;
    target_date?: string;
    generated_at?: string;
    sessions_scanned?: number;
    methodology_note?: string;
  };
  context?: {
    date_label?: string;
    situation_summary?: string;
    todays_market?: Record<string, number | string>;
    sector_performance?: Record<string, number>;
    first_half_context?: Record<string, unknown>;
  };
  analogue_framework?: {
    two_lens_approach?: {
      description?: string;
      situational_analogue_purpose?: string;
      numerical_analogue_purpose?: string;
      regime_analogue_purpose?: string;
    };
    three_lens_approach?: {
      description?: string;
      situational_analogue_purpose?: string;
      numerical_analogue_purpose?: string;
      regime_analogue_purpose?: string;
    };
  };
  situational_analogue?: AnalogueSituational | null;
  numerical_analogue?: AnalogueNumerical | null;
  regime_analogue?: AnalogueRegime | null;
  danger_template?: AnalogueDangerTemplate | null;
  dangerous_upcoming_window?: AnalogueDangerWindow | null;
  synthesis?: {
    summary?: string;
    analog_resolution_scorecard?: Record<string, unknown>;
    sectors_benefited_across_analogs?: { sector?: string; why?: string; held_up_in?: string[] }[];
    sectors_harmed_across_analogs?: { sector?: string; why?: string }[];
    consistent_loser?: string | null;
    consistent_rotation_winner?: string | null;
  };
  outlook?: {
    tactical_next_1_2_weeks?: { name?: string; tickers?: string[]; rationale?: string }[];
    highest_conviction?: { name?: string; rationale?: string }[];
    underweight?: { name?: string; tickers?: string[]; rationale?: string }[];
  };
  three_catalysts?: {
    description?: string;
    catalysts?: AnalogueCatalyst[];
  };
  composite_verdict?: AnalogueCompositeVerdict | null;
}

export interface MarketPredictionResponse {
  success: boolean;
  reference_date: string;
  filename?: string;
  available_dates?: string[];
  summary?: MarketPredictionSummary;
  prediction: Record<string, unknown>;
  market_analysis_excerpt?: MarketAnalysisExcerpt | null;
  scorecard?: MarketPredictionScorecard | null;
  analogue_analysis?: MarketAnalogueAnalysis | null;
}

export interface MarketPredictionListResponse {
  success: boolean;
  count: number;
  predictions: MarketPredictionSummary[];
}

export const marketPredictionService = {
  list: async (limit = 10): Promise<MarketPredictionListResponse | null> => {
    try {
      return await apiFetch<MarketPredictionListResponse>(
        `/api/market/predictions?limit=${limit}`,
      );
    } catch (error) {
      console.error('Error listing market predictions:', error);
      return null;
    }
  },

  getLatest: async (): Promise<MarketPredictionResponse | null> => {
    try {
      return await apiFetch<MarketPredictionResponse>('/api/market/predictions/latest');
    } catch (error) {
      console.error('Error loading latest market prediction:', error);
      return null;
    }
  },

  getByDate: async (referenceDate: string): Promise<MarketPredictionResponse | null> => {
    try {
      return await apiFetch<MarketPredictionResponse>(
        `/api/market/predictions/${encodeURIComponent(referenceDate)}`,
      );
    } catch (error) {
      console.error(`Error loading market prediction for ${referenceDate}:`, error);
      return null;
    }
  },
};

export interface CrossDayNarrativeDayEntry {
  date: string;
  read: string;
  engine_state?: string;
  label?: string;
}

export interface CrossDayNarrativeCurrentState {
  as_of?: string;
  summary: string;
  confidence?: string;
  trend?: string;
  implication?: string;
  primary_tell_to_watch?: string;
}

export interface CrossDayNarrativePrimary {
  id: string;
  title: string;
  headline?: string;
  thesis?: string;
  body?: string;
  base_case_direction?: string;
  risk_skew?: string;
  confidence?: string;
  confidence_basis?: string;
  day_by_day?: CrossDayNarrativeDayEntry[];
  current_state?: CrossDayNarrativeCurrentState;
}

export interface CrossDayNarrativeSecondary {
  id: string;
  title: string;
  thesis?: string;
  arc?: string;
  current_state?: string | CrossDayNarrativeCurrentState;
  trend_direction?: string;
  confidence?: string;
  day_by_day?: CrossDayNarrativeDayEntry[];
}

export interface CrossDaySituationToMonitor {
  rank: number;
  title: string;
  body: string;
}

export interface CrossDayDangerousWindow {
  title: string;
  body: string;
}

export interface CrossDayWindowSession {
  date: string;
  label: string;
  spy_pct: number;
  qqq_pct: number;
  dia_pct: number;
  xlk_pct: number;
  vix: number;
  vix_chg_pct: number;
  ten_year_yield_pct: number;
  fragility: string;
  sectors_up: number;
  sectors_down: number;
  leaders: string;
  laggards: string;
}

export interface CrossDayAnalogueNarrative {
  metadata: {
    reference_date: string;
    window_start: string;
    window_end: string;
    sessions_covered: number;
    schema_version?: number;
    design_principle?: string;
    methodology_note?: string;
    how_to_read?: string;
  };
  window_tape_reference?: {
    description?: string;
    sessions: CrossDayWindowSession[];
    xlk_whipsaw_sequence?: {
      xlk_pct: number[];
      dates: string[];
      interpretation?: string;
    };
  };
  primary_narrative: CrossDayNarrativePrimary;
  secondary_narratives: CrossDayNarrativeSecondary[];
  situations_to_monitor?: CrossDaySituationToMonitor[];
  dangerous_upcoming_window?: CrossDayDangerousWindow;
  synthesis: {
    description?: string;
    as_of?: string;
    body?: string;
    the_one_thesis?: string;
    primary_narrative_state?: {
      narrative: string;
      state: string;
      direction?: string;
    };
    secondary_narratives_state?: Array<{
      narrative: string;
      state: string;
      change?: string;
    }>;
    convergent_forward_shape?: {
      near_term_1_2_weeks?: string;
      reversal_risk_10_sessions?: string;
      drawdown_risk_63_sessions?: string;
      the_fork?: string;
    };
    key_branching_point?: string;
    primary_risk?: string;
    primary_opportunity?: string;
    primary_tell_to_watch?: string;
    how_this_differs_from_prior_versions?: string;
  };
}

export interface CrossDayAnalogueResponse {
  success: boolean;
  reference_date: string;
  filename: string;
  narrative: CrossDayAnalogueNarrative;
}

export const crossDayAnalogueService = {
  getLatest: async (): Promise<CrossDayAnalogueResponse | null> => {
    try {
      return await apiFetch<CrossDayAnalogueResponse>('/api/market/cross-day-analogue/latest');
    } catch (error) {
      console.error('Error loading latest cross-day analogue narrative:', error);
      return null;
    }
  },

  getByDate: async (referenceDate: string): Promise<CrossDayAnalogueResponse | null> => {
    try {
      return await apiFetch<CrossDayAnalogueResponse>(
        `/api/market/cross-day-analogue/${encodeURIComponent(referenceDate)}`,
      );
    } catch (error) {
      console.error(`Error loading cross-day analogue narrative for ${referenceDate}:`, error);
      return null;
    }
  },
};

export interface SectorAnalysisPriorDay {
  date?: string;
  percent_change?: number;
  close?: number;
  catalyst?: string;
  primary_catalyst?: string;
}

export interface SectorAnalysisMarketOverlay {
  themes?: string[];
  source?: string;
}

export interface SectorAnalysisPayload {
  sector_folder: string;
  proxy_label: string;
  reference_date: string;
  reference_date_label: string;
  percent_change: number;
  close: number | null;
  primary_catalyst: string | null;
  summary: string | null;
  sector_context: string | null;
  secondary_catalysts: string[];
  match_dimensions: string[];
  prior_day: SectorAnalysisPriorDay | null;
  market_overlay: SectorAnalysisMarketOverlay | null;
  method?: string | null;
  analog_count: number;
  lens: import('@/mocks/stockAnalogMock').StockAnalogLensData;
  lenses?: import('@/mocks/stockAnalogMock').StockAnalogLensData[];
  exa: {
    summary?: string;
    sector_context?: string;
    primary_catalyst?: string;
    secondary_catalysts?: string[];
    notable_constituents?: string[];
  } | null;
  meta?: Record<string, unknown>;
}

export interface SectorAnalysisResponse {
  success: boolean;
  analysis: SectorAnalysisPayload;
}

export const sectorAnalysisService = {
  getLatest: async (opts?: {
    sectorFolder?: string;
    date?: string;
    topK?: number;
  }): Promise<SectorAnalysisResponse | null> => {
    try {
      const params = new URLSearchParams();
      if (opts?.sectorFolder) params.set('sector_folder', opts.sectorFolder);
      if (opts?.date) params.set('date', opts.date);
      if (opts?.topK != null) params.set('top_k', String(opts.topK));
      const qs = params.toString();
      return await apiFetch<SectorAnalysisResponse>(
        `/api/market/sector-analysis/latest${qs ? `?${qs}` : ''}`,
      );
    } catch (error) {
      console.error('Error loading sector analysis:', error);
      return null;
    }
  },
};

export interface MarketTradeForwardHorizon {
  median_pct: number;
  mean_pct?: number;
  positive?: number;
  total?: number;
  hit_rate?: number;
}

export interface MarketTradeIdea {
  symbol: string;
  name?: string | null;
  direction: 'long' | 'short' | string;
  asset_type?: 'etf' | 'stock' | 'index' | 'commodity' | string | null;
  conviction?: 'high' | 'medium' | 'low' | string | null;
  timeframe?: string | null;
  thesis?: string | null;
  entry_trigger?: string | null;
  risk?: string | null;
  tags?: string[];
  forward_returns?: {
    t5?: MarketTradeForwardHorizon;
    t10?: MarketTradeForwardHorizon;
    t20?: MarketTradeForwardHorizon;
  } | null;
  analog_count?: number | null;
  headline_label?: string | null;
  headline_pct?: number | null;
  headline_detail?: string | null;
}

export interface MarketTradeIdeasPayload {
  metadata?: {
    reference_date?: string;
    generated_at?: string;
    generator?: string;
    model?: string;
    source?: string;
    analog_count?: number;
    trade_count?: number;
  };
  situation_headline?: string | null;
  trades: MarketTradeIdea[];
  disclaimer?: string | null;
}

export interface ReliableAnalogPick {
  rank?: number;
  symbol: string;
  hit_rate?: number | null;
  median_pct?: number | null;
  median_excess_pct?: number | null;
  samples?: number | null;
}

export interface ReliableAnalogPicksPayload {
  reference_date?: string;
  rule?: {
    min_hit_rate?: number;
    min_median_pct?: number;
    select_horizon?: number;
    top_n?: number;
  };
  weights?: { situ?: number; mech?: number; macro?: number };
  exclude_recent?: number;
  min_date?: string;
  analog_count?: number;
  analog_dates?: string[];
  picks: ReliableAnalogPick[];
  symbols?: string[];
  disclaimer?: string | null;
}

export interface UniverseAnalogStock {
  symbol: string;
  name?: string;
  exchange?: string | null;
  direction?: 'long' | 'short' | string;
  theme_id?: string;
  theme_label?: string;
  quality_tier?: string;
  quality_score?: number;
  samples?: number;
  median_pct?: number | null;
  mean_pct?: number | null;
  hit_rate?: number | null;
  best_pct?: number | null;
  worst_pct?: number | null;
  conviction?: string | null;
  headline_label?: string | null;
  headline_pct?: number | null;
  headline_detail?: string | null;
  forward_returns?: MarketTradeIdea['forward_returns'];
}

export interface UniverseAnalogGroup {
  id: string;
  label: string;
  description?: string;
  kind?: 'quality' | 'theme' | string;
  count?: number;
  stocks: UniverseAnalogStock[];
}

export interface UniverseAnalogWinnersPayload {
  metadata?: {
    reference_date?: string;
    generated_at?: string;
    grouped_at?: string;
    generator?: string;
    grouper?: string;
    analog_count?: number;
    universe_size?: number;
    liquid_symbols?: number;
    winner_count?: number;
    loser_count?: number;
  };
  summary?: {
    headline?: string;
    top_core?: string[];
    top_themes?: string[];
  };
  quality_groups?: UniverseAnalogGroup[];
  theme_groups?: UniverseAnalogGroup[];
  disclaimer?: string | null;
}

export interface MarketAnalysisPayload {
  proxy_label: string;
  reference_date: string;
  reference_date_label: string;
  percent_change: number;
  close?: number | null;
  primary_catalyst?: string | null;
  summary?: string | null;
  sector_context?: string | null;
  secondary_catalysts?: string[];
  match_dimensions?: string[];
  prior_day?: SectorAnalysisPriorDay | null;
  market_overlay?: Record<string, unknown> | null;
  method?: string | null;
  analog_count: number;
  lens: import('@/mocks/stockAnalogMock').StockAnalogLensData;
  lenses?: import('@/mocks/stockAnalogMock').StockAnalogLensData[];
  trade_ideas?: MarketTradeIdeasPayload | null;
  reliable_picks?: ReliableAnalogPicksPayload | null;
  universe_winners?: UniverseAnalogWinnersPayload | null;
  exa?: SectorAnalysisPayload['exa'];
  meta?: Record<string, unknown>;
}

export interface MarketAnalysisResponse {
  success: boolean;
  analysis: MarketAnalysisPayload;
}

export interface TradeStrategyYtdPerformance {
  ytd_return_pct: number;
  ytd_benchmark_pct?: number | null;
  ytd_excess_pct?: number | null;
  ytd_label?: string;
  ytd_as_of?: string | null;
  hit_rate_pct?: number | null;
  trade_count?: number;
  performance_source?: string | null;
  performance_profile?: string | null;
  book?: string | null;
}

export interface TradeStrategySummary {
  id: string;
  rank: number;
  strategy_name: string;
  strategy_description: string;
  direction: string;
  summary: string;
  reference_date: string;
  entry_date?: string | null;
  horizon?: string | null;
  generator?: string | null;
  trade_count: number;
  long_count: number;
  short_count: number;
  avoid_count?: number;
  tickers: string[];
  avoid_tickers?: string[];
  tags: string[];
  filename?: string;
  conviction_filter?: string | null;
  ytd_performance?: TradeStrategyYtdPerformance | null;
}

export interface TradeStrategyDetail extends TradeStrategySummary {
  metadata?: Record<string, unknown>;
  situation_headline?: string | null;
  playbook?: Record<string, unknown> | null;
  lenses?: Record<string, unknown> | null;
  summary_block?: Record<string, unknown> | null;
  trades: MarketTradeIdea[];
  disclaimer?: string | null;
}

export interface TradeStrategiesPayload {
  success?: boolean;
  reference_date: string;
  as_of_label: string;
  conviction_filter?: string | null;
  strategies: TradeStrategySummary[];
}

export interface TradeStrategyResponse {
  success: boolean;
  reference_date: string;
  strategy: TradeStrategyDetail;
}

export const tradeStrategiesService = {
  getLatest: async (
    date?: string,
    opts?: { conviction?: string },
  ): Promise<TradeStrategiesPayload | null> => {
    try {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      params.set('conviction', opts?.conviction ?? 'high');
      const qs = params.toString();
      return await apiFetch<TradeStrategiesPayload>(
        `/api/market/trade-strategies/latest${qs ? `?${qs}` : ''}`,
      );
    } catch (error) {
      console.error('Error loading trade strategies:', error);
      return null;
    }
  },

  getStrategy: async (
    referenceDate: string,
    strategyId: string,
    opts?: { conviction?: string },
  ): Promise<TradeStrategyResponse | null> => {
    try {
      const params = new URLSearchParams();
      params.set('conviction', opts?.conviction ?? 'high');
      const qs = params.toString();
      return await apiFetch<TradeStrategyResponse>(
        `/api/market/trade-strategies/${encodeURIComponent(referenceDate)}/${encodeURIComponent(strategyId)}?${qs}`,
      );
    } catch (error) {
      console.error('Error loading trade strategy:', error);
      return null;
    }
  },
};

export type MarketResearchArticleTag = {
  label: string;
  tone: 'success' | 'info' | 'neutral' | 'warning';
};

export type MarketResearchArticleSleeve = {
  label: string;
  tone: string;
  tickers: string[];
};

export type MarketResearchArticle = {
  article_id: string;
  date: string;
  id?: string;
  route_slug?: string;
  nav_title?: string;
  title: string;
  as_of: string;
  as_of_label: string;
  released_label: string;
  audience: string;
  summary: string;
  tags: MarketResearchArticleTag[];
  body_start_heading?: string;
  portfolio_snapshot?: {
    title?: string;
    sleeves?: MarketResearchArticleSleeve[];
    how_picked?: string;
    full_list_note?: string;
  };
  markdown: string;
};

export type MarketResearchArticleSummary = Pick<
  MarketResearchArticle,
  | 'article_id'
  | 'date'
  | 'route_slug'
  | 'nav_title'
  | 'title'
  | 'as_of'
  | 'as_of_label'
  | 'released_label'
  | 'audience'
  | 'summary'
  | 'tags'
>;

export type MarketResearchArticlesPayload = {
  success: boolean;
  reference_date: string;
  articles: MarketResearchArticleSummary[];
};

export type MarketResearchArticleResponse = {
  success: boolean;
  article: MarketResearchArticle;
};

export const marketResearchArticlesService = {
  getLatest: async (date?: string): Promise<MarketResearchArticlesPayload | null> => {
    try {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      const qs = params.toString();
      return await apiFetch<MarketResearchArticlesPayload>(
        `/api/market/research-articles/latest${qs ? `?${qs}` : ''}`,
      );
    } catch (error) {
      console.error('Error loading research articles:', error);
      return null;
    }
  },

  getArticle: async (
    referenceDate: string,
    articleId: string,
  ): Promise<MarketResearchArticleResponse | null> => {
    try {
      return await apiFetch<MarketResearchArticleResponse>(
        `/api/market/research-articles/${encodeURIComponent(referenceDate)}/${encodeURIComponent(articleId)}`,
      );
    } catch (error) {
      console.error('Error loading research article:', error);
      return null;
    }
  },
};

export const marketAnalysisService = {
  getLatest: async (opts?: {
    date?: string;
    topK?: number;
  }): Promise<MarketAnalysisResponse | null> => {
    try {
      const params = new URLSearchParams();
      if (opts?.date) params.set('date', opts.date);
      if (opts?.topK != null) params.set('top_k', String(opts.topK));
      const qs = params.toString();
      return await apiFetch<MarketAnalysisResponse>(
        `/api/market/analysis/latest${qs ? `?${qs}` : ''}`,
      );
    } catch (error) {
      console.error('Error loading market analysis:', error);
      return null;
    }
  },
};

export const majorStoriesService = {
  getLatest: async (): Promise<MajorStoriesPayload | null> => {
    try {
      return await apiFetch<MajorStoriesPayload>('/api/market/major-stories/latest');
    } catch (error) {
      console.error('Error loading major stories:', error);
      return null;
    }
  },

  getByWeekEnd: async (weekEndDate: string): Promise<MajorStoriesPayload | null> => {
    try {
      return await apiFetch<MajorStoriesPayload>(`/api/market/major-stories/${weekEndDate}`);
    } catch (error) {
      console.error('Error loading major stories:', error);
      return null;
    }
  },
};

export type Glp1StorylineChartEvent = {
  date: string;
  label: string;
  summary: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  percentChange?: number | null;
  primaryCatalyst?: string | null;
  glp1Context?: string | null;
  secondaryCatalysts?: string[] | null;
  symbol?: string;
  citations?: Array<{ url: string; title: string }> | null;
};

export type Glp1StorylineChartEventsPayload = {
  success?: boolean;
  storylineId: string;
  eventsCatalog: Array<{ date: string; label: string; sentiment: string }>;
  tickers: Array<{ symbol: string; label: string; priceSymbol: string }>;
  eventsBySymbol: Record<string, Glp1StorylineChartEvent[]>;
};

export const storylineChartEventsService = {
  getGlp1DisruptionEvents: async (): Promise<Glp1StorylineChartEventsPayload | null> => {
    try {
      return await apiFetch<Glp1StorylineChartEventsPayload>(
        '/api/market/storylines/glp1-disruption/chart-events',
      );
    } catch (error) {
      console.error('Error loading GLP-1 storyline chart events:', error);
      return null;
    }
  },
};

export type ValueChainProductSummary = {
  product: string;
  product_slug: string;
  ticker: string;
  status?: string;
  hops_completed?: number;
};

export const valueChainService = {
  listProducts: async (): Promise<{ products: ValueChainProductSummary[] }> => {
    return apiFetch<{ products: ValueChainProductSummary[] }>('/api/value-chain/products');
  },
  getGraph: async (ticker: string, slug: string) => {
    return apiFetch<import('@/types/valueChain').ValueChainGraph>(
      `/api/value-chain/${encodeURIComponent(ticker)}/${encodeURIComponent(slug)}/graph`,
    );
  },
  getStatus: async (ticker: string, slug: string): Promise<ValueChainProductSummary> => {
    return apiFetch<ValueChainProductSummary>(
      `/api/value-chain/${encodeURIComponent(ticker)}/${encodeURIComponent(slug)}/status`,
    );
  },
  getStarlinkTrade: async (ticker = 'SPCX') => {
    return apiFetch<import('@/components/value-chain/starlinkComtrade').ComtradeMapPayload>(
      `/api/value-chain/${encodeURIComponent(ticker)}/starlink/trade`,
    );
  },
  getStarlinkMarkets: async (ticker = 'SPCX') => {
    return apiFetch<import('@/components/value-chain/starlinkMarkets').StarlinkMarketsPayload>(
      `/api/value-chain/${encodeURIComponent(ticker)}/starlink/markets`,
    );
  },
  getStarlinkDashboard: async (ticker = 'SPCX') => {
    return apiFetch<import('@/components/value-chain/starlinkDashboard').StarlinkDashboardPayload>(
      `/api/value-chain/${encodeURIComponent(ticker)}/starlink/dashboard`,
    );
  },
};

export const timeAndSalesService = {
  getTrades: async (
    ticker: string,
    date: string,
    filter: TimeAndSalesFilter = 'largest',
    limit = 100,
    includeExtended = false,
    timeWindow?: { timeStart?: string; timeEnd?: string },
  ): Promise<TimeAndSalesResponse | null> => {
    try {
      const params = new URLSearchParams({
        filter,
        limit: String(limit),
        include_extended: String(includeExtended),
      });
      const ts = timeWindow?.timeStart?.trim();
      const te = timeWindow?.timeEnd?.trim();
      if (ts) params.set('time_start', ts);
      if (te) params.set('time_end', te);
      return await apiFetch<TimeAndSalesResponse>(
        `/api/stocks/${ticker.toUpperCase()}/time-and-sales/${date}?${params}`,
      );
    } catch (error) {
      console.error('Error fetching time-and-sales:', error);
      return null;
    }
  },
};

export type MerlinChatRole = 'user' | 'assistant';

export type MerlinChatTurn = {
  role: MerlinChatRole;
  content: string;
};

export type MerlinChatResponse = {
  reply: string;
  sessionId?: string;
  jobId?: string | null;
  jobStatus?: string | null;
  ticker?: string | null;
  kind?: string | null;
  board?: CohortBoardPayload | null;
};

export type VicChatMeta = {
  default_as_of?: string;
  sleeves?: string[];
  local_only?: boolean;
};

export type VicChatJob = {
  id: string;
  status: string;
  markdown?: string | null;
  error?: string | null;
  ticker?: string | null;
  board?: CohortBoardPayload | null;
};

type VicChatApiResponse = {
  session: { id: string };
  assistant?: { content?: string; kind?: string; board?: CohortBoardPayload | null };
  job?: VicChatJob | null;
  intent?: { ticker?: string | null };
};

function buildMockMerlinReply(query: string): string {
  const q = query.toLowerCase();

  if (q.includes('glp-1') || q.includes('ozempic') || q.includes('wegovy')) {
    return (
      'The GLP-1 storyline tracks how obesity and diabetes drug adoption is reshaping pharma, med-tech, and consumer staples.\n\n' +
      'Winners tend to be innovators and contract manufacturers; losers include certain food, beverage, and legacy diabetes names when volume guidance shifts.\n\n' +
      'On Guidance Gap, open Storylines → GLP-1 to see event-level stock charts and narrative context.'
    );
  }

  if (q.includes('sector') || q.includes('leading')) {
    return (
      'Sector leadership rotates with macro regime — rates, dollar, and earnings revisions all matter.\n\n' +
      'Check the Sector Analysis card on your dashboard for SOX analogs and catalyst narrative, or ask about a specific sector (semis, biotech, energy).'
    );
  }

  if (q.includes('major stor') || q.includes('this week') || q.includes('moved market')) {
    return (
      'Major Stories captures the week\'s dominant market narratives — macro catalysts, policy, and cross-asset themes.\n\n' +
      'Open Major Stories from the dashboard for ranked cards with detail views. I can also help connect a story to tickers or sectors if you name one.'
    );
  }

  return (
    `Good question. I'm still connecting to the full Guidance Gap research corpus, but I can help you navigate what's already in the platform.\n\n` +
    `Try asking about a ticker, a storyline (e.g. GLP-1), sector themes, or "what moved markets this week." Your question was: "${query.trim()}"`
  );
}

export const merlinService = {
  sendMessage: async (
    query: string,
    history: MerlinChatTurn[] = [],
    options: {
      sessionId?: string | null;
      job?: string;
      ticker?: string;
      asOf?: string;
    } = {},
  ): Promise<MerlinChatResponse> => {
    if (MERLIN_USE_VIC_CHAT) {
      const data = await apiFetch<VicChatApiResponse>('/api/vic-chat/messages', {
        method: 'POST',
        body: JSON.stringify({
          text: query,
          session_id: options.sessionId || undefined,
          job: options.job || 'auto',
          ticker: options.ticker || undefined,
          as_of: options.asOf || undefined,
        }),
      });
      return {
        reply: data.assistant?.content || '',
        sessionId: data.session.id,
        jobId: data.job?.id ?? null,
        jobStatus: data.job?.status ?? null,
        ticker: data.intent?.ticker ?? null,
        kind: data.assistant?.kind ?? null,
        board: data.assistant?.board ?? null,
      };
    }

    if (MERLIN_USE_MOCK) {
      await new Promise((resolve) => setTimeout(resolve, 600 + Math.random() * 400));
      return { reply: buildMockMerlinReply(query) };
    }

    return apiFetch<MerlinChatResponse>('/api/merlin/chat', {
      method: 'POST',
      body: JSON.stringify({ query, history }),
    });
  },

  getVicMeta: async (): Promise<VicChatMeta> => {
    return apiFetch<VicChatMeta>('/api/vic-chat/meta');
  },

  getVicJob: async (jobId: string): Promise<VicChatJob> => {
    return apiFetch<VicChatJob>(`/api/vic-chat/jobs/${jobId}`);
  },
};
